// /src/js/composables/useGlobalTheme.js
import { ref, computed, watch } from 'vue';
// import { useTheme } from 'vuetify';

// Global state - shared across all components
const currentTheme = ref('light');
const isInitialized = ref(false);

// Storage key for theme preference
const THEME_STORAGE_KEY = 'app-theme-preference';

/**
 * Global theme composable for managing light/dark mode across the entire app
 * @returns {Object} Theme state and methods
 */
export function useGlobalTheme() {
  // Computed properties
  const isDark = computed(() => currentTheme.value === 'dark');
  const isLight = computed(() => currentTheme.value === 'light');

  /**
   * Toggle between light and dark themes
   */
  const toggleTheme = () => {
    currentTheme.value = currentTheme.value === 'light' ? 'dark' : 'light';
    saveThemePreference();
    applyThemeToDocument();
  };

  /**
   * Set a specific theme
   * @param {string} theme - 'light' or 'dark'
   */
  const setTheme = (theme) => {
    if (theme === 'light' || theme === 'dark') {
      currentTheme.value = theme;
      saveThemePreference();
      applyThemeToDocument();
    } else {
      console.warn('[useGlobalTheme] Invalid theme value:', theme);
    }
  };

  /**
   * Save theme preference to localStorage
   */
  const saveThemePreference = () => {
    try {
      localStorage.setItem(THEME_STORAGE_KEY, currentTheme.value);
    } catch (error) {
      console.warn('[useGlobalTheme] Failed to save theme preference:', error);
    }
  };

  /**
   * Load theme preference from localStorage or detect system preference
   */
  const loadThemePreference = () => {
    try {
      // Try to get saved preference
      const savedTheme = localStorage.getItem(THEME_STORAGE_KEY);
      
      if (savedTheme && (savedTheme === 'light' || savedTheme === 'dark')) {
        currentTheme.value = savedTheme;
        console.log('[useGlobalTheme] Loaded saved theme:', savedTheme);
        return;
      }
    } catch (error) {
      console.warn('[useGlobalTheme] Failed to load theme preference:', error);
    }

    // Default to light mode (not using system preference)
    currentTheme.value = 'light';
    console.log('[useGlobalTheme] Using default theme: light');
  };

  /**
   * Apply theme to document root for CSS variables and classes
   */
  const applyThemeToDocument = () => {
    try {
      const root = document.documentElement;
      
      // Remove existing theme classes
      root.classList.remove('theme-light', 'theme-dark');
      
      // Add current theme class
      root.classList.add(`theme-${currentTheme.value}`);
      
      // Set data attribute for CSS selectors
      root.setAttribute('data-theme', currentTheme.value);
    } catch (error) {
      console.warn('[useGlobalTheme] Failed to apply theme to document:', error);
    }
  };

  /**
   * Initialize theme system
   */
  const initializeTheme = () => {
    if (isInitialized.value) {
      return; // Already initialized
    }

    loadThemePreference();
    applyThemeToDocument();
    
    // Watch for system theme changes
    try {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleSystemThemeChange = (e) => {
        // Only update if user hasn't set a manual preference
        const hasManualPreference = localStorage.getItem(THEME_STORAGE_KEY);
        if (!hasManualPreference) {
          currentTheme.value = e.matches ? 'dark' : 'light';
          applyThemeToDocument();
          console.log('[useGlobalTheme] System theme changed:', currentTheme.value);
        }
      };

      // Modern browsers
      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener('change', handleSystemThemeChange);
      } else {
        // Fallback for older browsers
        mediaQuery.addListener(handleSystemThemeChange);
      }
    } catch (error) {
      console.warn('[useGlobalTheme] Failed to setup system theme listener:', error);
    }

    isInitialized.value = true;
    console.log('[useGlobalTheme] Theme system initialized');
  };

  /**
   * Get theme icon for UI display
   */
  const getThemeIcon = () => {
    return isDark.value ? 'salon:weather-sunny' : 'salon:weather-night';
  };

  /**
   * Get theme toggle aria label for accessibility
   */
  const getToggleAriaLabel = () => {
    return isDark.value ? 'Switch to light mode' : 'Switch to dark mode';
  };

  // Watch for theme changes to apply to document
  watch(currentTheme, () => {
    if (isInitialized.value) {
      applyThemeToDocument();
    }
  });

  return {
    // State
    currentTheme: computed(() => currentTheme.value),
    isDark,
    isLight,
    isInitialized: computed(() => isInitialized.value),

    // Methods
    toggleTheme,
    setTheme,
    initializeTheme,
    
    // UI Helpers
    getThemeIcon,
    getToggleAriaLabel,
  };
}

// Export a singleton instance for consistent state
let globalThemeInstance = null;

export function useGlobalThemeSingleton() {
  if (!globalThemeInstance) {
    globalThemeInstance = useGlobalTheme();
  }
  return globalThemeInstance;
}

export default useGlobalTheme;
